<script setup lang="ts">
const { modalType } = storeToRefs(useLayoutStore());

const {
  isGoogleButtonActive,
  isFacebookButtonActive,
  isTwitchButtonActive,
  isSteamButtonActive,
  isSocialAuthEnabled,
} = useFeatureFlagsNew();

const { isSocialAuthFlow, socialService, clearSocialData } = useAuthSocial();

const { $socialAuth } = useNuxtApp();

type Props = {
  buttonsOnly?: boolean;
  withTitles?: boolean;
  isLogin?: boolean;
};

const {
  buttonsOnly = false,
  withTitles = false,
  isLogin = false,
} = defineProps<Props>();

const buttonHeight = computed(() => (buttonsOnly ? '32px' : '40px'));

function onGoogleButtonClick() {
  $socialAuth.provider('google').login();
}

function onFacebookButtonClick() {
  $socialAuth.provider('facebook').login();
}

function onTwitchButtonClick() {
  $socialAuth.provider('twitch').login();
}

function onSteamButtonClick() {
  $socialAuth.provider('steam').login();
}

const isSocialButtonsVisible = computed(() => {
  return !isSocialAuthFlow.value || isLogin;
});

const isSocialCancelButtonVisible = computed(() => {
  return isSocialAuthFlow.value && !isLogin;
});
</script>

<template>
  <div
    v-if="isSocialAuthEnabled"
    class="gap-[15px] flex flex-col"
  >
    <div
      v-if="!buttonsOnly"
      class="w-full"
    >
      <div
        aria-orientation="horizontal"
        class="w-full flex items-center gap-[15px] select-none"
        role="separator"
      >
        <span class="h-px flex-1 bg-white/50"></span>
        <span
          class="justify-center text-white/50 uppercase text-[14px] leading-none"
        >
          {{
            modalType === 'login'
              ? $t('auth.register.sign_in_through')
              : $t('auth.register.sign_up_through')
          }}
        </span>
        <span class="h-px flex-1 bg-white/30"></span>
      </div>
    </div>

    <div :class="[$style.buttons, { [$style.vertical]: withTitles }]">
      <button
        v-if="isGoogleButtonActive && isSocialButtonsVisible"
        @click.prevent="onGoogleButtonClick"
      >
        <NuxtIcon
          aria-hidden="true"
          filled
          name="social/google-color"
        />
        <span v-if="withTitles">
          {{
            isLogin
              ? $t('auth.login.login_with', { type: 'Google' })
              : $t('auth.register.register_with', { type: 'Google' })
          }}
        </span>
      </button>

      <button
        v-if="isFacebookButtonActive && isSocialButtonsVisible"
        @click.prevent="onFacebookButtonClick"
      >
        <NuxtIcon
          aria-hidden="true"
          filled
          name="social/facebook-color"
        />
        <span v-if="withTitles">
          {{
            isLogin
              ? $t('auth.login.login_with', { type: 'Facebook' })
              : $t('auth.register.register_with', { type: 'Facebook' })
          }}
        </span>
      </button>

      <button
        v-if="isTwitchButtonActive && isSocialButtonsVisible"
        @click.prevent="onTwitchButtonClick"
      >
        <NuxtIcon
          aria-hidden="true"
          filled
          name="social/twitch-color"
        />
        <span v-if="withTitles">
          {{
            isLogin
              ? $t('auth.login.login_with', { type: 'Twitch' })
              : $t('auth.register.register_with', { type: 'Twitch' })
          }}
        </span>
      </button>

      <button
        v-if="isSteamButtonActive && isSocialButtonsVisible"
        @click.prevent="onSteamButtonClick"
      >
        <NuxtIcon
          aria-hidden="true"
          filled
          name="social/steam-color"
        />
        <span v-if="withTitles">
          {{
            isLogin
              ? $t('auth.login.login_with', { type: 'Steam' })
              : $t('auth.register.register_with', { type: 'Steam' })
          }}
        </span>
      </button>

      <button
        v-if="isSocialCancelButtonVisible"
        :class="$style.cancelButton"
        @click.prevent="clearSocialData"
      >
        <span v-if="withTitles">
          {{
            $t('auth.register.cancel_register_with', { type: socialService })
          }}
        </span>
      </button>
    </div>
  </div>
</template>

<style module lang="scss">
.socialAuth {
  @apply flex flex-col gap-2;
}

.title {
  @apply mt-6 mb-3 flex items-center text-center gap-4 text-auth-texts/50 uppercase text-xs;

  &:before,
  &:after {
    content: '';
    @apply h-[1px] bg-auth-texts/10 flex-1;
  }
}

.buttons {
  @apply flex w-full items-center justify-start; /* troquei justify-between -> justify-start */
  gap: 15px; /* gap fixo */
  @apply flex-nowrap; /* opcional, evita quebra */

  button {
    @apply flex-1 h-11 py-3 bg-[#0C1B44] rounded-[10px] inline-flex justify-center items-center gap-2.5 text-white;

    height: v-bind(buttonHeight);

    &.cancelButton {
      @apply opacity-50 text-auth-texts/40 hover:opacity-40;
    }
  }

  &.vertical {
    @apply flex-col;
    gap: 15px; /* equivalente ao gap-2 */

    button {
      @apply w-full h-11 py-3 bg-[#0C1B44] rounded-[10px] inline-flex justify-center items-center gap-2.5 text-white;

      &.cancelButton {
        @apply opacity-50 text-auth-texts/70 hover:opacity-40;
      }
    }
  }
}
</style>
